import { useState } from "react";
import { Database, FileText, Image, Link2, MoreHorizontal, PencilLine, Share2, Trash2, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { KnowledgeBase } from "@/mocks/knowledge";
import { cn } from "@/lib/utils";

interface Props {
  kb: KnowledgeBase;
  onDelete?: (id: string) => void;
  onRename?: (id: string, name: string) => void;
}

function autoTagFromName(name: string): string {
  const v = (name || "").trim();
  if (!v) return "知识";
  // Prefer first 2 CJK characters
  const cjk = Array.from(v).filter((ch) => /[\u4e00-\u9fff]/.test(ch));
  if (cjk.length >= 2) return `${cjk[0]}${cjk[1]}`;
  if (cjk.length === 1) {
    const rest = Array.from(v).filter((ch) => /[A-Za-z0-9]/.test(ch));
    return `${cjk[0]}${(rest[0] || "库").toString().toUpperCase()}`.slice(0, 2);
  }
  // Fallback: first 2 alnum characters
  const alnum = Array.from(v).filter((ch) => /[A-Za-z0-9]/.test(ch));
  if (alnum.length >= 2) return `${alnum[0]}${alnum[1]}`.toUpperCase();
  return v.slice(0, 2);
}

export default function KnowledgeBaseCard({ kb, onDelete, onRename }: Props) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameVal, setNameVal] = useState(kb.name);
  const autoTag = autoTagFromName(kb.name);

  const handleMenuAction = (action: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (action === "rename") setRenaming(true);
    if (action === "delete") onDelete?.(kb.id);
  };

  const handleRenameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRename?.(kb.id, nameVal);
    setRenaming(false);
  };

  return (
    <div
      className="group rounded-3xl border border-white/70 bg-white/80 shadow-[0_10px_40px_rgba(15,23,42,0.05)] backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_50px_rgba(15,23,42,0.08)]"
      onClick={() => navigate(`/knowledge/${kb.id}`)}
      onMouseLeave={() => setMenuOpen(false)}
    >
      <div className="relative h-40 rounded-t-3xl bg-muted">
        {/* Keep cover image clipped, but allow menus to overflow */}
        <div className="absolute inset-0 overflow-hidden rounded-t-3xl">
          <img src={kb.coverImage} alt={kb.name} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
          <span className="absolute left-3 top-3 rounded-full bg-muted/80 px-2.5 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm">知识库</span>
        </div>

        <div className="absolute right-3 top-3 z-20 opacity-0 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-white/80 text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
            aria-label="更多"
            type="button"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-[60] mt-2 min-w-40 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg">
              {[
                { key: "rename", icon: PencilLine, label: "重命名" },
                { key: "share-link", icon: Share2, label: "分享链接" },
                { key: "share-img", icon: Image, label: "分享图片" },
                { key: "delete", icon: Trash2, label: "删除", danger: true },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    onClick={(e) => handleMenuAction(item.key, e)}
                    type="button"
                    className={
                      item.danger
                        ? "group flex w-full items-center gap-2 rounded-lg bg-white px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-white hover:text-destructive"
                        : "flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                    }
                  >
                    <Icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        item.danger ? "text-destructive group-hover:text-destructive" : "text-muted-foreground",
                      )}
                    />
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        {renaming ? (
          <form onSubmit={handleRenameSubmit} onClick={(e) => e.stopPropagation()} className="mb-3">
            <input
              autoFocus
              value={nameVal}
              onChange={(e) => setNameVal(e.target.value)}
              className="h-10 w-full rounded-lg border border-primary bg-background px-3 text-sm font-medium outline-none ring-offset-background focus:ring-2 focus:ring-ring"
              onBlur={handleRenameSubmit}
            />
          </form>
        ) : (
          <h3 className="mb-1 line-clamp-1 text-base font-semibold tracking-tight text-foreground">{kb.name}</h3>
        )}
        <p className="mb-3 h-12 overflow-hidden text-sm leading-6 text-muted-foreground line-clamp-2">{kb.description}</p>

        <div className="mb-4 flex flex-wrap gap-2">
          <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">#{autoTag}</span>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="truncate">{kb.updatedAt} 更新</span>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Database className="h-3.5 w-3.5" />{kb.size}</span>
            <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{kb.fileCount}</span>
            <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{kb.memberCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
